// Domain helpers ported from src/data/seed.ts, counting.ts, pluralize.ts

// ---- pluralize ----
const IRREGULAR = { person: "people", child: "children", foot: "feet", tooth: "teeth", man: "men", woman: "women" };
const VOWELS = new Set(["a","e","i","o","u"]);

function pluralizeSingleWord(word) {
  const lower = word.toLowerCase();
  if (IRREGULAR[lower]) {
    const p = IRREGULAR[lower];
    if (word[0] === word[0].toUpperCase()) return p[0].toUpperCase() + p.slice(1);
    return p;
  }
  const last = lower.slice(-1);
  const lastTwo = lower.slice(-2);
  if (last === "y" && lower.length > 1 && !VOWELS.has(lower.slice(-2, -1))) return word.slice(0, -1) + "ies";
  if (last === "s" || last === "x" || last === "z" || lastTwo === "ch" || lastTwo === "sh") return word + "es";
  return word + "s";
}

export function pluralizeLabel(label) {
  const parts = label.trim().split(/\s+/);
  if (!parts.length) return label;
  const connectors = /^(with|of|or|and|for|from|to|the|a|an)$/i;
  if (parts.some((t) => connectors.test(t))) return label + "s";
  const last = parts[parts.length - 1];
  return [...parts.slice(0, -1), pluralizeSingleWord(last)].join(" ");
}

export function labelForCount(count, singular, pluralOverride) {
  if (count === 1) return singular;
  if (pluralOverride && pluralOverride.trim()) return pluralOverride;
  return pluralizeLabel(singular);
}

export function formatCount(count, singular, pluralOverride) {
  return `${count} ${labelForCount(count, singular, pluralOverride)}`;
}

// ---- lookups ----
export const personById = (people, id) => id ? people.find((p) => p.id === id) : undefined;
export const projectById = (projects, id) => projects.find((p) => p.id === id);

export const activePledges = (projectId, pledges) =>
  pledges.filter((p) => p.projectId === projectId && p.status === "active");

export const awaitingPledges = (projectId, pledges) =>
  pledges.filter((p) => p.projectId === projectId && p.status === "awaiting-recommit");

export const pledgedForReq = (reqId, pledges) =>
  pledges.filter((p) => p.requirementId === reqId && p.status === "active").reduce((s, p) => s + p.quantity, 0);

export const requirementIsMet = (req, pledges) => pledgedForReq(req.id, pledges) >= req.needed;

export const peopleRequirements = (project) => project.requirements.filter((r) => r.kind === "people");
export const thingRequirements = (project) => project.requirements.filter((r) => r.kind === "thing");

export const totalPeopleNeeded = (project) => peopleRequirements(project).reduce((s, r) => s + r.needed, 0);

export const totalPeopleCommitted = (project, pledges) =>
  peopleRequirements(project).reduce((s, r) => s + Math.min(r.needed, pledgedForReq(r.id, pledges)), 0);

export const allRequirementsMet = (project, pledges) =>
  project.requirements.every((r) => requirementIsMet(r, pledges));

// Live UI status. If the DB still says "gathering" or "threshold-crossed"
// but every requirement is actually met, the UI should immediately render
// as "It's Happening" — never leave a fully-met project stuck on Almost There.
export function effectiveStatus(project, pledges) {
  const s = project.status;
  if ((s === "gathering" || s === "threshold-crossed") && allRequirementsMet(project, pledges)) {
    return "threshold-crossed";
  }
  return s;
}

// Derive the compact-card format from a project's semantic effective status.
// Kept alongside `effectiveStatus` so status → card-format is the single
// source of truth. ProjectCardCompact reads this internally; callers no
// longer pass a `variant` prop (that was the source of the Profile
// "It's Happening" mislabel bug).
//
//   completed / closed-not-met / cancelled → "story"      (archival tile)
//   threshold-crossed / happening          → "happening"  (medium card)
//   everything else (gathering, draft…)    → "gathering"  (neutral compact)
export function compactFormatFor(project, pledges) {
  const status = effectiveStatus(project, pledges);
  if (status === "completed" || status === "closed-not-met" || status === "cancelled") {
    return "story";
  }
  if (status === "threshold-crossed" || status === "happening") {
    return "happening";
  }
  return "gathering";
}

// ---- library visibility (shared by Home previews + Category page) ----
// A project is "library-visible" iff it would render inside its own
// category library page (`/commons/:categoryId`). Encoded as the single
// source of truth so Home previews and Category shelves can never drift
// apart: Home filters previews through this predicate before applying
// the editorial `homePreview` flag; Category uses it while building
// its render plan.
//
// Rules mirror `Category.jsx#buildRenderPlan`:
//   1. Drafts are hidden from public discovery.
//   2. If the project is part of a retry chain, the chain's current
//      (tail) attempt must itself not be a draft — a chain whose latest
//      chapter is still private is not yet public.
//   3. If the project is a historical attempt in a chain whose tail
//      lives in a different category, it renders in the tail's
//      category, not here — so it's not visible from this side.
// Current (tail) attempt of a retry chain; the project itself if standalone.
export function chainTail(project, allProjects) {
  const byId = new Map(allProjects.map((p) => [p.id, p]));
  let tail = project;
  const guard = new Set([tail.id]);
  while (tail.nextAttemptId && byId.has(tail.nextAttemptId)) {
    const next = byId.get(tail.nextAttemptId);
    if (guard.has(next.id)) break;
    guard.add(next.id);
    tail = next;
  }
  return tail;
}

// Shared Overflow rule. A retry chain is one unit: its current attempt
// decides for every attempt. Visibility only — never permissions.
export const isOverflow = (project, allProjects) =>
  chainTail(project, allProjects).libraryPlacement === "overflow";

export function isLibraryVisible(project, allProjects) {
  if (!project || project.status === "draft") return false;
  if (isOverflow(project, allProjects)) return false;
  if (!project.nextAttemptId && !project.previousAttemptId) return true;
  const tail = chainTail(project, allProjects);
  if (tail.status === "draft") return false;
  // Historicals are only rendered by the tail's category library.
  if (tail.category !== project.category) return false;
  return true;
}



export function projectProgress(project, pledges) {
  const totalNeeded = project.requirements.reduce((s, r) => s + r.needed, 0);
  if (!totalNeeded) return 0;
  const totalPledged = project.requirements.reduce(
    (s, r) => s + Math.min(r.needed, pledgedForReq(r.id, pledges)),
    0
  );
  return totalPledged / totalNeeded;
}

export const openRequirements = (project, pledges) =>
  project.requirements.filter((r) => !requirementIsMet(r, pledges));

export const personIsIn = (personId, projectId, pledges) =>
  pledges.some((p) => p.personId === personId && p.projectId === projectId && p.status === "active");

export const personPledges = (personId, projectId, pledges) =>
  pledges.filter((p) => p.personId === personId && p.projectId === projectId && p.status === "active");

export function pendingInvitationsFor(personId, phone, invitations) {
  if (!personId && !phone) return [];
  return invitations.filter((i) =>
    i.status === "pending" &&
    ((personId && i.inviteePersonId === personId) || (phone && i.inviteePhone === phone))
  );
}

export function inviterForProject(personId, project, invitations) {
  if (!personId || personId === project.stewardId) return undefined;
  const inv = invitations.find((i) => i.projectId === project.id && i.inviteePersonId === personId);
  return inv?.inviterId;
}

export const isStewardOrCo = (personId, project) =>
  project.stewardId === personId || (project.coStewardIds || []).includes(personId);

// You page lists (also the source of the Home "You" count).
const STEWARDING_STATUSES = ["draft", "gathering", "threshold-crossed", "happening"];

export const youStewarding = (personId, projects) =>
  projects.filter(
    (p) => isStewardOrCo(personId, p) && STEWARDING_STATUSES.includes(p.status) && !isOverflow(p, projects)
  );

export const youJoined = (personId, projects, pledges) =>
  projects.filter(
    (p) => personIsIn(personId, p.id, pledges) && !isStewardOrCo(personId, p) && !isOverflow(p, projects)
  );

export const youProjectsCount = (personId, projects, pledges) =>
  youStewarding(personId, projects).length + youJoined(personId, projects, pledges).length;

// ---- counting ----
export function uniqueHelpers(projectId, pledges) {
  const seen = new Set();
  const out = [];
  for (const p of pledges) {
    if (p.projectId !== projectId || p.status !== "active") continue;
    if (seen.has(p.personId)) continue;
    seen.add(p.personId);
    out.push(p.personId);
  }
  return out;
}
export const uniqueHelpersCount = (id, pledges) => uniqueHelpers(id, pledges).length;

export function uniqueParticipants(project, pledges) {
  const peopleReqIds = new Set(peopleRequirements(project).map((r) => r.id));
  const seen = new Set();
  const out = [];
  for (const p of activePledges(project.id, pledges)) {
    if (!peopleReqIds.has(p.requirementId)) continue;
    if (seen.has(p.personId)) continue;
    seen.add(p.personId);
    out.push(p.personId);
  }
  return out;
}
export const uniqueParticipantsCount = (project, pledges) => uniqueParticipants(project, pledges).length;

export const attendanceCount = (project, pledges) =>
  project.status === "completed" ? uniqueParticipantsCount(project, pledges) : 0;

export const missingPeople = (project, pledges) =>
  peopleRequirements(project).reduce((s, r) => s + Math.max(0, r.needed - pledgedForReq(r.id, pledges)), 0);

export const missingThings = (project, pledges) =>
  thingRequirements(project).reduce((s, r) => s + Math.max(0, r.needed - pledgedForReq(r.id, pledges)), 0);

export const commitmentsAreMutable = (project) =>
  project.status === "gathering" || project.status === "threshold-crossed";

export function buildProjectParentMap(project, pledges, people, invitations) {
  const committed = new Set(activePledges(project.id, pledges).map((p) => p.personId));
  committed.add(project.stewardId);
  const byId = new Map(people.map((p) => [p.id, p]));
  const parentMap = new Map();
  const invs = invitations || [];
  for (const id of committed) {
    if (!byId.has(id) || id === project.stewardId) continue;
    // Only real recorded invitation edges create parent links. Founder =
    // root. Missing invitations produce an unattached participant — the
    // backend invariant tests (test_participation_invariant.py) prevent
    // this state from arising in practice; the visualization no longer
    // manufactures a steward attachment to hide broken provenance.
    const inv = invs.find((i) => i.projectId === project.id && i.inviteePersonId === id);
    if (inv?.inviterId && committed.has(inv.inviterId) && inv.inviterId !== id) {
      parentMap.set(id, inv.inviterId);
    }
  }
  return parentMap;
}

// ---- Participation model helpers (mirror of server-side gate) -------
// Derived, not stored. See server.py "Participation model" block.

export const hasActivePeoplePledge = (personId, project, pledges) => {
  const peopleReqIds = new Set(peopleRequirements(project).map((r) => r.id));
  return pledges.some(
    (p) => p.personId === personId
      && p.projectId === project.id
      && p.status === "active"
      && peopleReqIds.has(p.requirementId)
  );
};

export const isActiveParticipant = (personId, project, pledges) =>
  isStewardOrCo(personId, project) || hasActivePeoplePledge(personId, project, pledges);

export const hasPendingInvitation = (personId, project, invitations) =>
  invitations.some(
    (i) => i.projectId === project.id
      && i.inviteePersonId === personId
      && i.status === "pending"
  );

export const mayPledgePeople = (personId, project, pledges, invitations) =>
  isActiveParticipant(personId, project, pledges)
    || hasPendingInvitation(personId, project, invitations);

export const mayPledgeThing = (personId, project, pledges) =>
  isActiveParticipant(personId, project, pledges);

export const mayInvite = (personId, project, pledges) =>
  isActiveParticipant(personId, project, pledges);
